import { useLayoutEffect, useState } from 'react'
import { Link } from 'react-router'
import { cx } from '../../../design/cx'
import {
  ALWAYS_OFFERED,
  goToSheetSection,
  SHEET_SECTIONS,
  type SheetSection,
} from './sheetSections'
import styles from './SheetNav.module.css'

export interface SheetNavProps {
  /**
   * Anything that changes which cards the sheet renders — the query's `dataUpdatedAt` and the
   * active shape. The nav re-reads the page whenever this changes: adding the first piece of
   * equipment makes a card appear, and the nav has to gain the entry for it.
   */
  revision: string
  /**
   * The character's gallery, when there is one to link to. It rides at the end of the same
   * row rather than off to one side: it is where the rest of this character's pictures are,
   * and a sheet with no portrait already shows it as a card in the stack below.
   */
  galleryTo?: string | undefined
}

/**
 * Quick navigation to the sheet's sections, in the space beside the portrait.
 *
 * The sheet is a dozen cards long and most of them collapse, so reaching "Rote spells" on a
 * phone is a scroll to find it and a tap to open it. These buttons do both at once.
 *
 * They are icons up to 900px and icons-with-words above it — the same treatment the shell
 * gives its own three tabs, at the wider of the two breakpoints, because twelve buttons need
 * more room to earn their labels than three do (the measurements are in the stylesheet). The
 * icon-only form keeps its label in the accessibility tree: an icon on its own says nothing
 * to a screen reader.
 *
 * **Which sections are worth offering is read from the DOM, not computed here.** Every card
 * decides for itself whether it renders — an empty equipment card is hidden from a reader who
 * cannot add to it, powers are filtered by the active shape, the magic cards depend on a
 * server flag — and each says whether it has anything in it, through `data-empty`. Restating
 * those rules here would be a second copy to keep in step, and the shape filter alone would
 * be enough to get wrong. So the nav asks the page two questions per section: did your card
 * land, and does it hold anything? {@link ALWAYS_OFFERED} names the three that are worth a
 * button either way.
 *
 * The read is in a layout effect, so it happens after the cards mount and before the browser
 * paints: the nav is never briefly wrong on screen.
 *
 * Each glyph is drawn in its card's colour — the same `--section-*` code the card's left
 * spine carries. On a phone that is the only thing telling a reader which button is which
 * before they have learned the icons, and it is the thing that ties the button to the card
 * it lands on.
 */
export function SheetNav({ revision, galleryTo }: SheetNavProps) {
  const [present, setPresent] = useState<SheetSection[]>([])

  useLayoutEffect(() => {
    setPresent((current) => {
      const found = SHEET_SECTIONS.filter((section) => {
        const card = document.getElementById(section.id)
        if (card === null) return false
        // A card an owner is only being shown so they can add to it. The card stays — that is
        // where the add control lives — but the nav does not offer a jump to "None yet.".
        return !card.hasAttribute('data-empty') || ALWAYS_OFFERED.includes(section.key)
      })
      // Same set, same array — otherwise every revision costs a render for no change.
      const same =
        found.length === current.length && found.every((section, i) => section === current[i])
      return same ? current : found
    })
  }, [revision])

  if (present.length === 0 && galleryTo === undefined) return null

  return (
    <nav className={styles.nav} aria-label="Sheet sections">
      {present.map((section) => (
        <button
          key={section.key}
          type="button"
          className={cx(styles.chip, styles[section.variant])}
          // Reads as "Go to Aspects" whether or not the word is on screen, and contains the
          // visible label so voice control can say what it sees (WCAG 2.5.3).
          aria-label={`Go to ${section.label}`}
          title={section.label}
          onClick={() => goToSheetSection(section.id)}
        >
          <span className={styles.icon}>{section.icon}</span>
          <span className={styles.label}>{section.label}</span>
        </button>
      ))}

      {/* Last, where the gallery card would be if the sheet were showing one. An anchor, not
          a button: this one leaves the page, and a destination should survive a middle-click.
          Ember rather than a section colour — it is the row's one control that is not a
          section, and the app's accent is how it says so. */}
      {galleryTo !== undefined && (
        <Link to={galleryTo} className={cx(styles.chip, styles.gallery)} title="Gallery">
          <span className={styles.icon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="8.5" cy="10" r="1.4" />
              <path d="M20.5 15.5 16 11l-5.5 6" />
            </svg>
          </span>
          <span className={styles.label}>Gallery</span>
        </Link>
      )}
    </nav>
  )
}
